import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useState, useEffect } from 'react';
import { Button } from '@werkstatt/ui';
import { useToast } from '../hooks/use-toast';
import api from '../lib/api';

interface PermissionGroup {
  group: string;
  permissions: { key: string; label: string }[];
}

interface Props {
  userId: string;
  isAdmin: boolean;
}

export function UserPermissionsEditor({ userId, isAdmin }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: definitions } = useQuery<{ groups: PermissionGroup[] }>({
    queryKey: ['permission-definitions'],
    queryFn: async () => {
      const res = await api.get('/permissions/definitions');
      return res.data;
    },
    staleTime: Infinity,
  });

  const { data: currentPerms, isLoading } = useQuery<{ permissions: string[]; is_admin: boolean }>({
    queryKey: ['permissions', userId],
    queryFn: async () => {
      const res = await api.get(`/permissions/${userId}`);
      return res.data;
    },
  });

  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (currentPerms) {
      setSelected(new Set(currentPerms.permissions));
    }
  }, [currentPerms]);

  const saveMutation = useMutation({
    mutationFn: async (permissions: string[]) => {
      await api.put(`/permissions/${userId}`, { permissions });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['permissions', userId] });
      toast({ title: 'Berechtigungen gespeichert' });
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: (isAxiosError(error) ? error.response?.data?.detail : undefined) || 'Fehler beim Speichern',
      });
    },
  });

  const togglePermission = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  if (isAdmin) {
    return (
      <div className="rounded-md bg-blue-500/10 border border-blue-500/20 p-4 text-sm text-blue-700 dark:text-blue-300">
        Admins haben automatisch alle Berechtigungen. Individuelle Berechtigungen können nur für reguläre Benutzer gesetzt werden.
      </div>
    );
  }

  if (isLoading || !definitions) {
    return <div className="text-sm text-muted-foreground py-4">Lade Berechtigungen...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {definitions.groups.map((group) => (
          <div key={group.group} className="border rounded-md p-3">
            <div className="font-medium text-sm mb-2">{group.group}</div>
            <div className="space-y-1">
              {group.permissions.map((perm) => (
                <label key={perm.key} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selected.has(perm.key)}
                    onChange={() => togglePermission(perm.key)}
                    className="rounded"
                  />
                  <span className="text-sm">{perm.label}</span>
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setSelected(new Set())}
        >
          Alle entfernen
        </Button>
        <Button
          size="sm"
          onClick={() => saveMutation.mutate(Array.from(selected))}
          disabled={saveMutation.isPending}
        >
          Berechtigungen speichern
        </Button>
      </div>
    </div>
  );
}
